/**
 * The one place a status becomes a colour.
 *
 * docs/brand-guidelines.md BG §18.4 is the rule; this is the rule in code. Five states, five colours,
 * and every badge, pill, table chip, graph node and sidebar count in the portal goes through here:
 *
 *   completed → green   succeeded, approved, accepted, healthy
 *   running   → amber   running, in progress, processing
 *   neutral   → grey    queued, pending, paused, cancelled, skipped, waiting on another run
 *   failed    → pink    failed, blocked, rejected
 *   action    → blue    waiting on a human: a gate, an approval, a decision
 *
 * Adding a status means adding it here, not inventing a colour at the call site. Anything unknown
 * falls through to `neutral`, which is the honest answer for a state nobody has classified yet.
 */
import type { StatusTone } from '@zerohuman/ui'

/** BG §18.4 — the five states. `system` and `plan` are chrome and never come out of a status. */
export type RunStatusTone = Extract<StatusTone, 'completed' | 'running' | 'neutral' | 'failed' | 'action'>

const TONE_BY_STATUS: Record<string, RunStatusTone> = {
    // Finished well.
    succeeded: 'completed',
    accepted: 'completed',
    approved: 'completed',
    // Moving right now.
    running: 'running',
    processing: 'running',
    // Parked: nothing is moving, and nothing is being asked of you.
    pending: 'neutral',
    queued: 'neutral',
    cancelled: 'neutral',
    skipped: 'neutral',
    skipped_overlap: 'neutral',
    /*
     * Both are genuinely stuck rather than pending a decision from you: one is queued behind another
     * run, the other is parked until the spend cap is raised or the window rolls over. Neither is a
     * gate, so neither gets the blue that means "this one is yours".
     */
    waiting_on_run: 'neutral',
    paused_spend: 'neutral',
    paused_plan: 'neutral',
    // Held because its team is switched off (#1809): the enterprise's own choice, parked until it is switched on.
    paused_team: 'neutral',
    // Held by a person's pause of its execution (#809): parked until someone resumes it.
    paused: 'neutral',
    // Ended badly, or cannot proceed.
    failed: 'failed',
    blocked: 'failed',
    stopped: 'failed',
    rejected: 'failed',
    delivered: 'completed',
    submitting: 'running',
    delayed: 'running',
    bounced: 'failed',
    complained: 'failed',
    unknown: 'action',
    // Waiting on a human.
    waiting: 'action',
    waiting_on_gate: 'action',
    // A work item's states (#1830): proposed waits on a person, in progress is a run working it.
    proposed: 'action',
    ready: 'neutral',
    held: 'neutral',
    in_progress: 'running',
    done: 'completed',
    dropped: 'neutral',
}

/*
 * Nullish is allowed because callers have it: a task that has never run, a graph node nobody
 * visited. Guarding here rather than at each call site is what keeps the components branch-free —
 * and keeps the fallback one decision, made once, instead of one per component.
 */
export function statusTone(status: string | null | undefined): RunStatusTone {
    return (status && TONE_BY_STATUS[status]) || 'neutral'
}

/** What the product calls this status out loud. Unknown statuses show their raw string, not a blank. */
export function statusLabel(status: string): string {
    return STATUS_LABELS[status] ?? status
}

/** Run and gate vocabulary as the product says it out loud. BG §22.3: the word carries the meaning, not the hue. */
export const STATUS_LABELS: Record<string, string> = {
    submitting: 'Submitting',
    accepted: 'Accepted',
    delivered: 'Delivered',
    delayed: 'Delivery delayed',
    bounced: 'Bounced',
    complained: 'Spam complaint',
    unknown: 'Delivery unconfirmed',
    succeeded: 'Completed',
    running: 'Running',
    queued: 'Queued',
    pending: 'Pending',
    waiting: 'Waiting for approval',
    waiting_on_gate: 'Waiting for approval',
    waiting_on_run: 'Waiting for a run',
    paused_spend: 'Spend paused',
    paused_plan: 'Plan paused',
    paused_team: 'Team off',
    paused: 'Paused',
    blocked: 'Blocked',
    stopped: 'Stopped',
    failed: 'Failed',
    cancelled: 'Cancelled',
    skipped: 'Skipped',
    skipped_overlap: 'Skipped overlap',
    proposed: 'Waiting for approval',
    ready: 'Ready',
    held: 'On hold',
    in_progress: 'In progress',
    done: 'Done',
    dropped: 'Dropped',
}

/**
 * The status an execution's pill shows. An execution that stopped on an outcome nothing routes reports `blocked`,
 * and while that stop is on the Blockers page it sits in the Blocked lane and says so. Once someone clears it or
 * newer work on its pipeline takes over, it moves to Complete (#557), where "Blocked" would contradict its lane:
 * there it reads "Stopped", the way a failed one there still reads "Failed".
 *
 * A waiting execution shows what it waits on: a run paused because its team is off reads "Team off", not "Waiting
 * for approval", which only a gate earns.
 */
export function executionPillStatus(execution: { status: string; lane: string; waitingOn?: string | null }): string {
    if (execution.status === 'waiting' && execution.waitingOn) return execution.waitingOn
    return execution.status === 'blocked' && execution.lane === 'complete' ? 'stopped' : execution.status
}

/**
 * The status a run's chip shows. A run that dead-ended (its task has no next step for the outcome it ended on) is
 * stored `succeeded`, and Blockers lists its pipeline as stopped: everywhere else it reads "stopped" too, never a
 * green success. The API says so with `stoppedReason`; any other run shows its own status.
 */
export function runPillStatus(run: { status: string; stoppedReason?: string | null }): string {
    return run.status === 'succeeded' && run.stoppedReason ? 'stopped' : run.status
}
